import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import getCases, { CANtrackCaseRaw } from 'api/comments/getCases';
import type { MyCaseSummary, PinnedCase } from 'containers/pages/cantrackDashboard/types';

interface DwpCaseSetupProps {
  myCases: MyCaseSummary[];
  pinnedCases: PinnedCase[];
  isReconfigure: boolean;
  onSelect: (caseId: number) => void;
  onCancel?: () => void;
}

interface CaseOption {
  id: number;
  title: string;
  customer: string;
  source: 'pinned' | 'my' | 'search';
}

function DwpCaseSetup({ myCases, pinnedCases, isReconfigure, onSelect, onCancel }: DwpCaseSetupProps) {
  const [selected, setSelected] = useState<CaseOption | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<CaseOption[]>([]);
  const [searching, setSearching] = useState(false);

  const pinnedOptions: CaseOption[] = pinnedCases.map((c) => ({
    id: c.Id,
    title: c.Title,
    customer: c.CustomerName,
    source: 'pinned' as const,
  }));

  const myCaseOptions: CaseOption[] = myCases
    .filter((c) => !pinnedCases.some((p) => p.Id === c.Id))
    .map((c) => ({
      id: c.Id,
      title: c.Title,
      customer: c.CustomerName,
      source: 'my' as const,
    }));

  const allOptions = [...pinnedOptions, ...myCaseOptions, ...searchResults];
  const uniqueOptions = allOptions.filter(
    (opt, idx, arr) => arr.findIndex((o) => o.id === opt.id) === idx,
  );

  useEffect(() => {
    if (inputValue.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { rawCases } = await getCases({ SearchKeyword: inputValue, Take: 20, Page: 1 });
        const results: CaseOption[] = rawCases.map((c: CANtrackCaseRaw) => ({
          id: Number(c.Id),
          title: c.Title,
          customer: c.CustomerName,
          source: 'search' as const,
        }));
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <InfoOutlinedIcon fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {isReconfigure
            ? 'Change your Daily Work Plan case.'
            : 'Select a case to use as your Daily Work Plan.'}
        </Typography>
        {isReconfigure && onCancel && (
          <Button size="small" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Autocomplete
          sx={{ flex: 1 }}
          size="small"
          options={uniqueOptions}
          value={selected}
          onChange={(_e, value) => setSelected(value)}
          inputValue={inputValue}
          onInputChange={(_e, value) => setInputValue(value)}
          getOptionLabel={(opt) => `#${opt.id} ${opt.title}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          groupBy={(opt) =>
            opt.source === 'pinned'
              ? 'Pinned Cases'
              : opt.source === 'my'
                ? 'My Cases'
                : 'Search Results'
          }
          loading={searching}
          filterOptions={(x) => x}
          renderOption={(props, opt) => (
            <li {...props} key={opt.id}>
              <Box>
                <Typography variant="body2">
                  #{opt.id} {opt.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {opt.customer}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search for a case"
              placeholder="Type case name or ID..."
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />,
                endAdornment: (
                  <>
                    {searching && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={inputValue.length < 2 ? 'Type to search...' : 'No cases found'}
        />
        <Button
          variant="contained"
          size="small"
          onClick={() => selected && onSelect(selected.id)}
          disabled={!selected}
          sx={{ mt: 0.25 }}
        >
          Save
        </Button>
      </Box>
    </Paper>
  );
}

export default DwpCaseSetup;
